import {messages} from '@smartmixin/i18n';
import type {MessageDescriptor} from 'react-intl';
import type {
  ForecastModelMode,
  ForecastModelStrategy,
} from '../../store/userPreference';

/**
 * The forecast-model preference, as a screen has to present it.
 *
 * The record itself (`forecast_model_pins` / `_mode` / `_strategy`) lives in
 * the userPreference slice; this module holds the derivations both apps need
 * and neither should own: which stations a pin actually wins at, how to label
 * that without the label growing with the account, and the mapping between the
 * four fallback choices a user sees and the two fields they write.
 *
 * Kept here rather than in either app because the astral cards showed what
 * transcribing a second copy costs (#170): 57% byte-identical code with three
 * defects carried across.
 */

/** Slots the screen offers. The server bound is 16, which is storage, not UX. */
export const PINS_MAX_UI = 3;

/**
 * Above this many stations the coverage label counts instead of naming.
 *
 * Sized from production: station names average 12.7 characters and reach 55,
 * accounts reach 1,410 stations, and the row's description slot is ~120
 * characters before it clamps. Three names fit; four do not, reliably.
 */
export const NAMED_STATIONS_MAX = 3;

/** A station reduced to what the coverage derivation needs. */
export type CoverageStation = {
  name: string;
  /** Server names accessible there — `state.location.forecastModels`. */
  models: string[];
};

export type PinCoverage = {
  /** Pin -> the stations it actually wins at, in station order. */
  wonBy: Record<string, string[]>;
  /**
   * Pin -> whether it reaches any station at all, ignoring the pins above it.
   *
   * Splits the two reasons a pin serves nowhere, which need different copy: it
   * is *shadowed* (an earlier pin already covers everything it reaches) or it
   * is *unreachable* (it covers none of the user's stations). Saying "a model
   * above covers all your stations" for a lone pin, or for the first pin, is
   * simply false.
   */
  reaches: Record<string, boolean>;
  /** Stations no pin reaches. The fallback answers for these. */
  uncovered: string[];
};

/**
 * Which stations each pin is actually served at.
 *
 * Mirrors the server's `resolve_preference`: the pins are walked in order and
 * the **first** one accessible at a station wins there. So a wide-domain pin
 * shadows every pin below it at every station it covers — AROME's domain
 * contains Berlin, so `[AROME, ICON_DE]` serves AROME at both and ICON-D2 at
 * neither. That state is invisible in the stored record, which is why the
 * screen derives and shows it.
 */
export const computePinCoverage = (
  pins: string[],
  stations: CoverageStation[],
): PinCoverage => {
  const wonBy: Record<string, string[]> = {};
  const reaches: Record<string, boolean> = {};
  pins.forEach((pin) => {
    wonBy[pin] = [];
    reaches[pin] = stations.some((station) => station.models.includes(pin));
  });
  const uncovered: string[] = [];

  stations.forEach((station) => {
    const winner = pins.find((pin) => station.models.includes(pin));
    if (winner === undefined) {
      uncovered.push(station.name);
      return;
    }
    // `find` returns the first match, so a duplicated pin never gets the
    // station twice.
    wonBy[winner].push(station.name);
  });

  return {wonBy, reaches, uncovered};
};

/**
 * Reorder the pins the client can render, leaving the ones it cannot in place.
 *
 * `items` on both screens is the pin list filtered through this build's
 * catalogue, and six models are commented out of `constants.ts` while more are
 * queued behind #112. A profile can therefore hold a pin this build cannot
 * draw, and writing the rendered list back would delete it -- silently, on the
 * first drag. So the permutation is applied only to the slots holding known
 * pins; unknown pins keep their absolute index and their precedence.
 */
export const reorderKnownPins = (
  pins: string[],
  known: string[],
  nextKnownOrder: string[],
): string[] => {
  const knownSet = new Set(known);
  let next = 0;
  return pins.map((pin) =>
    knownSet.has(pin) && next < nextKnownOrder.length
      ? nextKnownOrder[next++]
      : pin,
  );
};

/**
 * What a pin row should say, bounded so the row height never depends on how
 * many stations the account has.
 */
export type CoverageLabel =
  /**
   * No station uses this pin. Overrides every other form, at any size.
   * `shadowed` = an earlier pin already covers everything it reaches;
   * `unreachable` = it covers none of the user's stations at all.
   */
  | {kind: 'shadowed'}
  | {kind: 'unreachable'}
  /** Single-station account: the per-station framing is noise. */
  | {kind: 'inUse'}
  | {kind: 'named'; stations: string[]}
  | {kind: 'all'; count: number}
  | {kind: 'some'; used: number; count: number};

export const coverageLabel = (
  hits: string[],
  totalStations: number,
  reachesAny = true,
): CoverageLabel => {
  if (hits.length === 0) {
    return reachesAny ? {kind: 'shadowed'} : {kind: 'unreachable'};
  }
  if (totalStations <= 1) {
    return {kind: 'inUse'};
  }
  if (totalStations <= NAMED_STATIONS_MAX) {
    return {kind: 'named', stations: hits};
  }
  if (hits.length === totalStations) {
    return {kind: 'all', count: totalStations};
  }
  return {kind: 'some', used: hits.length, count: totalStations};
};

/**
 * What the user picks for "everywhere else".
 *
 * Four options over two fields. `national` is deliberately absent: it ranked
 * `(regional-before-global, finest mesh)` with no notion of a country, so it
 * returned AROME in Berlin, and it never differed from `resolution` anywhere
 * populated. It was retired backend-side.
 */
export type FallbackOption = 'blend' | 'resolution' | 'ecmwf' | 'gfs';

export const FALLBACK_OPTIONS: FallbackOption[] = [
  'blend',
  'resolution',
  'ecmwf',
  'gfs',
];

type PreferenceFields = {
  mode: ForecastModelMode;
  strategy: ForecastModelStrategy | null;
};

/**
 * The two fields an option writes.
 *
 * `mode` is always explicit, including for the strategies: the server reads
 * the mode before the strategy, so leaving a stale `chain` behind would make
 * the chosen strategy unreachable.
 */
export const fallbackToPreference = (
  option: FallbackOption,
): PreferenceFields => {
  switch (option) {
    case 'blend':
      return {mode: 'chain', strategy: null};
    case 'resolution':
      return {mode: 'single', strategy: 'resolution'};
    case 'ecmwf':
      return {mode: 'single', strategy: 'ecmwf'};
    default:
      // GFS is `strategy: null` — "no opinion, server default" — which is also
      // what the 27.5k untouched profiles hold. Writing an explicit marker
      // instead would migrate them on first render.
      return {mode: 'single', strategy: null};
  }
};

/**
 * Which option a stored preference represents.
 *
 * Total by construction: an all-null profile, and a profile holding a strategy
 * this build no longer knows (a device that synced before `national` was
 * retired), both read as GFS — which is what the server serves them.
 */
export const preferenceToFallback = (
  mode: ForecastModelMode | null,
  strategy: ForecastModelStrategy | null,
): FallbackOption => {
  if (mode === 'chain') {
    return 'blend';
  }
  if (strategy === 'resolution' || strategy === 'ecmwf') {
    return strategy;
  }
  return 'gfs';
};

/**
 * The message a coverage label renders as, and the values it needs.
 *
 * Returns the descriptor rather than a formatted string so `utils/` never
 * touches `useIntl`: calling it here would drag react-intl and the Redux store
 * into a leaf module and break both apps' partial `@smartmixin/core` mocks. The
 * caller formats.
 *
 * Extracted because the two apps' switch statements were byte-identical over 28
 * lines. That is the shape the `cross-app-features` seam check flags, and it is
 * exactly how the astral cards ended up 57% duplicated with three defects
 * carried across (#170).
 */
export const coverageMessage = (
  label: CoverageLabel,
): {
  descriptor: MessageDescriptor;
  values?: Record<string, string | number>;
} => {
  switch (label.kind) {
    case 'shadowed':
      return {descriptor: messages.forecastModels.pinShadowed};
    case 'unreachable':
      return {descriptor: messages.forecastModels.pinUnreachable};
    case 'inUse':
      return {descriptor: messages.forecastModels.pinInUse};
    case 'named':
      return {
        descriptor: messages.forecastModels.pinUsedAt,
        values: {stations: label.stations.join(', ')},
      };
    case 'all':
      return {
        descriptor: messages.forecastModels.pinUsedAtAll,
        values: {count: label.count},
      };
    default:
      return {
        descriptor: messages.forecastModels.pinUsedAtSome,
        values: {used: label.used, count: label.count},
      };
  }
};

/** The message naming the stations no pin reaches, or null when all are covered. */
export const uncoveredMessage = (
  uncovered: string[],
  totalStations: number,
): {
  descriptor: MessageDescriptor;
  values: Record<string, string | number>;
} | null => {
  if (uncovered.length === 0) {
    return null;
  }
  return totalStations <= NAMED_STATIONS_MAX
    ? {
        descriptor: messages.forecastModels.pinsUncoveredNamed,
        values: {stations: uncovered.join(', '), count: uncovered.length},
      }
    : {
        descriptor: messages.forecastModels.pinsUncoveredCount,
        values: {used: uncovered.length, count: totalStations},
      };
};

/** Title and second line for each "everywhere else" option, in display order. */
export const FALLBACK_MESSAGES: Record<
  FallbackOption,
  {label: MessageDescriptor; description: MessageDescriptor}
> = {
  blend: {
    label: messages.forecastModels.fallbackBlend,
    description: messages.forecastModels.fallbackBlendSub,
  },
  resolution: {
    label: messages.forecastModels.fallbackResolution,
    description: messages.forecastModels.fallbackResolutionSub,
  },
  ecmwf: {
    label: messages.forecastModels.fallbackEcmwf,
    description: messages.forecastModels.fallbackEcmwfSub,
  },
  gfs: {
    label: messages.forecastModels.fallbackGfs,
    description: messages.forecastModels.fallbackGfsSub,
  },
};

/**
 * What the free-account banner should say the user is served right now.
 *
 * Takes the **account** size, not the number of stations whose coverage has
 * loaded: "Right now: GFS at all 0 stations" is reachable on any genuine cold
 * start, and "at all 2 stations" on a five-station account is simply wrong.
 */
export const servedNowMessage = (
  totalStations: number,
): {descriptor: MessageDescriptor; values?: Record<string, number>} =>
  totalStations === 0
    ? {descriptor: messages.forecastModels.servedNowNone}
    : {
        descriptor: messages.forecastModels.servedNowMany,
        values: {count: totalStations},
      };

/**
 * Whether the profile holds any forecast preference at all.
 *
 * Pristine is all-null, which is what 27.5k profiles that never touched the
 * setting hold and what the server reads as "no opinion, GFS-first". Note
 * `mode: 'single'` with a null strategy *resolves* identically to pristine but
 * is not pristine -- it is a value the user chose, and the reset exists to get
 * back from it.
 */
export const hasForecastPreference = (
  pins: string[] | null,
  mode: ForecastModelMode | null,
  strategy: ForecastModelStrategy | null,
): boolean => (pins?.length ?? 0) > 0 || mode !== null || strategy !== null;

/**
 * The payload that returns a profile to pristine.
 *
 * All three fields, explicitly null. Naming all three is deliberate here even
 * though the PATCH is key-presence based: that is exactly what makes the
 * backend re-derive `forecast_model_order` from them, and a reset wants the
 * legacy mirror cleared too. Everywhere else, send only what changed.
 */
export const resetPreference = (): {
  pins: null;
  mode: null;
  strategy: null;
} => ({pins: null, mode: null, strategy: null});
